import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Clock, Users, Printer, User } from 'lucide-react';

const TimesheetReportView = () => {
  const { postId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const from = searchParams.get('from') || 'ready-to-publish';
  
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState('all');

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  useEffect(() => {
    fetchTimesheetReport();
  }, [postId]);

  const fetchTimesheetReport = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/api/ai-posts/${postId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setPost(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load timesheet report');
      console.error('Error fetching timesheet report:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (from === 'ready-to-publish') {
      navigate('/ai-ready-to-publish');
    } else if (from === 'published') {
      navigate('/ai-published-posts');
    } else if (from === 'timesheet-management') {
      const tab = searchParams.get('tab') || 'timesheets';
      navigate(`/timesheet-management?activeTab=${tab}`);
    } else {
      navigate(-1);
    }
  };

  // Get selected employee data
  const getSelectedEmployeeData = () => {
    const employeeData = post?.timesheet_data?.employee_data || [];
    if (selectedEmployee === 'all') {
      return employeeData;
    }
    return employeeData.filter(emp => emp.user_id === selectedEmployee);
  };

  const formatDateRange = (start, end) => {
    if (!start || !end) return 'Date range not available';
    
    // Handle date-only strings (YYYY-MM-DD) without timezone conversion
    let startDate, endDate;
    
    if (start.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = start.split('-');
      startDate = new Date(year, month - 1, day);
    } else {
      startDate = new Date(start);
    }
    
    if (end.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = end.split('-');
      endDate = new Date(year, month - 1, day);
    } else {
      endDate = new Date(end);
    }
    
    const formatOptions = {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    };
    
    return `${startDate.toLocaleDateString('en-US', formatOptions)} to ${endDate.toLocaleDateString('en-US', formatOptions)}`;
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading timesheet report...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Report not found'}</p>
          <button
            onClick={handleBack}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const timesheetData = post.timesheet_data || {};
  const employeeData = timesheetData.employee_data || [];
  const selectedEmployeeData = getSelectedEmployeeData();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Header - Styled like PaystubReportView */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-lg font-medium text-gray-900">
              Timesheet Report
            </h1>
            
            <div className="flex items-center space-x-3">
              <button 
                onClick={handlePrint}
                className="inline-flex items-center px-3 py-1.5 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Report
              </button>
              
              <button
                onClick={handleBack}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </button>
            </div>
          </div>
          
          <div className="flex items-center text-sm text-gray-600">
            <Calendar className="h-4 w-4 mr-1" />
            Period: {formatDateRange(timesheetData.start_date, timesheetData.end_date)}
          </div>
        </div>

        {/* Summary Cards - More compact like PaystubReportView */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-xl font-bold text-theme-blue">{timesheetData.total_employees}</div>
            <div className="text-xs text-gray-600">Total Employees</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-xl font-bold text-green-600">{timesheetData.total_hours?.toFixed(2)}</div>
            <div className="text-xs text-gray-600">Total Hours</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-xl font-bold text-purple-600">{timesheetData.regular_hours?.toFixed(2)}</div>
            <div className="text-xs text-gray-600">Regular Hours</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-xl font-bold text-orange-600">${timesheetData.total_cost?.toFixed(2)}</div>
            <div className="text-xs text-gray-600">Total Gross</div>
          </div>
        </div>

        {/* Employee Filter - Styled like PaystubReportView */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <User className="h-4 w-4 text-gray-400" />
              <label className="text-sm font-medium text-gray-700">View Employee:</label>
            </div>
            <div className="flex-1 max-w-xs">
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="all">All Employees Summary</option>
                {employeeData.map((employee) => (
                  <option key={employee.user_id} value={employee.user_id}>
                    {employee.user_name} - {employee.total_hours?.toFixed(2)}h
                  </option>
                ))}
              </select>
            </div>
            {selectedEmployee !== 'all' && (
              <button
                onClick={() => setSelectedEmployee('all')}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Show All
              </button>
            )}
          </div>
        </div>

        {/* Employee Details */}
        {selectedEmployee === 'all' ? (
          // All Employees Summary
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">All Employees Summary</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Hours</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Regular Hours</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">After Hours</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hourly Rate</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Pay</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {employeeData.map((employee) => (
                    <tr key={employee.user_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-6 w-6 rounded-full bg-theme-blue flex items-center justify-center text-white text-xs font-medium">
                            {employee.user_name.charAt(0)}
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{employee.user_name}</div>
                            <div className="text-xs text-gray-500">{employee.user_role}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {employee.total_hours?.toFixed(2)}h
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {employee.regular_hours?.toFixed(2)}h
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {employee.after_hours_hours?.toFixed(2)}h
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        ${employee.hourly_rate?.toFixed(2)}/hr
                        <div className="text-xs text-gray-500">
                          ${employee.after_hours_rate?.toFixed(2)}/hr after hours
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-theme-blue">
                        ${employee.total_pay?.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                  {/* Totals Row */}
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">TOTALS</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {employeeData.reduce((sum, emp) => sum + (emp.total_hours || 0), 0).toFixed(2)}h
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {employeeData.reduce((sum, emp) => sum + (emp.regular_hours || 0), 0).toFixed(2)}h
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {employeeData.reduce((sum, emp) => sum + (emp.after_hours_hours || 0), 0).toFixed(2)}h
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">-</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-theme-blue">
                      ${timesheetData.total_cost?.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          // Individual Employee Details
          selectedEmployeeData.map((employee) => (
            <div key={employee.user_id} className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-8 w-8 rounded-full bg-theme-blue flex items-center justify-center text-white text-sm font-medium">
                      {employee.user_name.charAt(0)}
                    </div>
                    <div className="ml-3">
                      <h2 className="text-base font-semibold text-gray-900">{employee.user_name}</h2>
                      <p className="text-xs text-gray-600">{employee.user_email} • {employee.user_role}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-theme-blue">{employee.total_hours?.toFixed(2)}h</div>
                    <div className="text-xs text-gray-500">${employee.total_pay?.toFixed(2)}</div>
                  </div>
                </div>
              </div>

              <div className="p-4">
                {/* Hours Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-lg font-bold text-theme-blue">{employee.total_hours?.toFixed(2)}</div>
                    <div className="text-xs text-gray-600">Total Hours</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-lg font-bold text-green-600">{employee.regular_hours?.toFixed(2)}</div>
                    <div className="text-xs text-gray-600">Regular Hours</div>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <div className="text-lg font-bold text-orange-600">{employee.after_hours_hours?.toFixed(2)}</div>
                    <div className="text-xs text-gray-600">After Hours</div>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <div className="text-lg font-bold text-purple-600">${employee.hourly_rate?.toFixed(2)}</div>
                    <div className="text-xs text-gray-600">Hourly Rate</div>
                  </div>
                </div>

                {/* Daily Entries Table */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Daily Breakdown</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Regular Hours</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">After Hours</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Hours</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Daily Pay</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {(employee.daily_entries || []).map((entry, dayIndex) => (
                          <tr key={dayIndex} className="hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                              {new Date(entry.date).toLocaleDateString('en-US', { 
                                weekday: 'short', 
                                month: 'short', 
                                day: 'numeric' 
                              })}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {entry.regular_hours?.toFixed(2) || '0.00'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {entry.after_hours?.toFixed(2) || '0.00'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                              {entry.total_hours?.toFixed(2) || '0.00'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                              ${((entry.regular_hours * employee.hourly_rate) + (entry.after_hours * employee.after_hours_rate)).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                        
                        {/* Employee Total Row */}
                        <tr className="bg-blue-50 border-t-2 border-blue-200">
                          <td className="px-3 py-2 whitespace-nowrap text-sm font-bold text-blue-900">Total:</td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm font-bold text-blue-900">
                            {employee.regular_hours?.toFixed(2) || '0.00'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm font-bold text-blue-900">
                            {employee.after_hours_hours?.toFixed(2) || '0.00'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm font-bold text-blue-900">
                            {employee.total_hours?.toFixed(2) || '0.00'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm font-bold text-blue-900">
                            ${employee.total_pay?.toFixed(2) || '0.00'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}

        {selectedEmployeeData.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <p className="text-gray-500">No employees match the current selection.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimesheetReportView;